import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { BaseTeamProps } from "@custom-types/teams-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useAppDispatch } from "@store/store-helper";
import { updateTeamDetails } from "@store/teams/teams-slice-thunk";
import {
  DEFAULT_INPUT_FONT_SIZE,
  inputPlaceholderItalicSx,
} from "@styles/common-styles";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Maximum number of rows for the description on mobile devices */
const MAX_ROW_MOBILE = 4;

/** Maximum number of rows for the description on desktop devices */
const MAX_ROW_DESKTOP = 14;

/** Maximum number of characters for markup's detail */
const MAX_INPUT_LENGTH = 500;

/**
 * This component shows the team description and allows to edit it if the user has permissions to do so.
 */
export function TeamDescription({ team }: BaseTeamProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const { isScreenMdOrSmaller } = useMediaQueryList();

  /**
   * This method it will update the description field
   *
   * @param value the description value
   */
  async function updateDescription(value: string): Promise<string> {
    try {
      trackEvent({
        name: TeamEvents.changeTeamDetails,
        props: { length: value.length, attribute: "description" },
      });

      setIsUpdating(true);

      await dispatch(
        updateTeamDetails({
          teamId: team.id,
          payload: { description: value },
        })
      ).unwrap();

      return value;
    } catch {
      return team.description ?? "";
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <FaroTextField
      maxRows={isScreenMdOrSmaller ? MAX_ROW_MOBILE : MAX_ROW_DESKTOP}
      isDisabled={isUpdating}
      isReadOnly={false}
      isMultiline={true}
      shouldClearOnBlur={false}
      isAdornmentOnBottom={true}
      shouldShowCharacterCount={true}
      maxInputLength={MAX_INPUT_LENGTH}
      placeholder="Add description here"
      initialValue={team.description || ""}
      onConfirmed={(newValue) => updateDescription(newValue.trim())}
      fontSize={DEFAULT_INPUT_FONT_SIZE}
      sx={{
        ...inputPlaceholderItalicSx,
        padding: "14px",
      }}
    />
  );
}
